import * as React from 'react'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import Link from '@mui/material/Link'

import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import SearchIcon from '@mui/icons-material/Search'
import Container from '@mui/material/Container'
import { useTheme } from '@mui/material/styles'

import { UserContext } from '../UserContext'
import FilterChips from '../components/FilterChips/Import'
import MenuItem from '../components/MenuItem'
import { generateMenuItems } from './ImportMenuItems'
import useMediaQuery from '@mui/material/useMediaQuery'

import { DialogsContext, DialogsProvider } from '../DialogsContext'

import { sendToAnalytics } from '../Analytics'
import { useTrackSearch } from '../hooks/useTrackSearch'

export default function ImportOptions(props) {
    const presetContext = props.contextName ? props.contextName : ''
    const presetPinnedNodes = props.pinnedNodes ? props.pinnedNodes : ''
    const presetDialog = props.importDialog ? props.importDialog : ''
    const presetText = props.presetText ? props.presetText : ''

    const { username } = React.useContext(UserContext)

    const {
        activeImportDialog,
        closeImportDialog,
        setActiveImportDialog,
        setPresetSearchString,
        setPresetContext,
    } = React.useContext(DialogsContext)

    const [selectedCategory, setSelectedCategory] = React.useState('files')
    const [categoryBeforeSearch, setCategoryBeforeSearch] = React.useState(
        'files'
    )
    const [searchQuery, setSearchQuery] = React.useState('')

    const trackSearch = useTrackSearch('import_page', username)

    const updateSelectedCategory = category => {
        setSelectedCategory(category)
        setCategoryBeforeSearch(category)
        sendToAnalytics({
            userId: username,
            event: 'choose_import_category',
            data: { category },
        })
    }

    const handleSearchChange = e => {
        const val = e.target.value
        setSearchQuery(val)
        if (val && selectedCategory !== 'import') {
            setSelectedCategory('import')
        } else if (
            !val &&
            selectedCategory === 'import' &&
            categoryBeforeSearch !== 'import'
        ) {
            setSelectedCategory(categoryBeforeSearch)
        }
        trackSearch(val)
    }

    React.useEffect(() => {
        if (presetDialog) setActiveImportDialog(presetDialog)
    }, [])

    const templateMenuItems = generateMenuItems(username)
    const categoryFiltered =
        selectedCategory === 'import'
            ? templateMenuItems
            : templateMenuItems.filter(({ category }) =>
                  category.includes(selectedCategory)
              )
    const filteredMenuItems = searchQuery
        ? categoryFiltered.filter(item => {
              const q = searchQuery.toLowerCase()
              return (
                  item.name.toLowerCase().includes(q) ||
                  (item.description &&
                      item.description.toLowerCase().includes(q))
              )
          })
        : categoryFiltered

    const isMobile = useMediaQuery('(max-width:600px)')

    const importingCommentPart1 = presetContext ? `importing to graph: ` : ''
    const importingSeparator = importingCommentPart1 ? ' | ' : ''
    const importingCommentPart2 = presetPinnedNodes
        ? `${importingSeparator} preselected terms: ${presetPinnedNodes}`
        : ''

    if (presetPinnedNodes) setPresetSearchString(presetPinnedNodes)
    if (presetContext) setPresetContext(presetContext)
    if (presetText) setPresetSearchString(presetText)

    return (
        <Box
            sx={{
                backgroundColor: theme => theme.palette.background.default,
                flexGrow: 1,
                ml: 4,
            }}
        >
            <Box sx={{ pt: 0, pb: 2, maxWidth: 800 }}>
                <Typography
                    component="subtitle1"
                    variant="subtitle1"
                    align="left"
                    color="text.primary"
                >
                    Generate a knowledge graph from any text, search results,
                    URLs, documents, or your own notes.
                </Typography>
            </Box>
            <Box
                sx={{
                    pt: 2,
                    pb: 1,
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 1, sm: 2 },
                }}
            >
                <Typography
                    component="h6"
                    variant="h6"
                    align="left"
                    color="text.primary"
                    sx={{ whiteSpace: { sm: 'nowrap' } }}
                >
                    Please, select the import source you'd like to use:
                </Typography>
                <TextField
                    size="small"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 18 }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ maxWidth: 220 }}
                />
            </Box>
            {(importingCommentPart1 || importingCommentPart2) && (
                <>
                    <Box
                        sx={{
                            pt: 2,
                            pb: 0,
                        }}
                    >
                        <Typography
                            component="subtitle1"
                            variant="subtitle1"
                            align="left"
                            color="text.primary"
                        >
                            {importingCommentPart1}
                            <Link href={`/${username}/${presetContext}/edit`}>
                                {presetContext}
                            </Link>
                            {importingCommentPart2}
                        </Typography>
                    </Box>
                </>
            )}
            <Grid
                container
                spacing={isMobile ? 0 : 2}
                sx={{ ml: isMobile ? -4 : -2 }}
            >
                <Grid item md={4} xs={6}>
                    <FilterChips
                        onClick={updateSelectedCategory}
                        selectedCategory={selectedCategory}
                    />
                </Grid>
                <Grid item sx={{ mt: 4 }} md={8} xs={6}>
                    {renderTemplateMenu(filteredMenuItems)}
                </Grid>
            </Grid>
        </Box>
    )
}

function renderTemplateMenu(menuItems) {
    const theme = useTheme()

    return (
        <Container
            sx={{
                py: 1,
                [theme.breakpoints.up('sm')]: {
                    pl: 0,
                },
            }}
            maxWidth={false}
        >
            <Grid
                container
                rowSpacing={3}
                justifyContent="flex-start"
                columnSpacing={3}
                alignItems="flex-start"
                pr={0}
            >
                {menuItems.map(card => (
                    <Grid item key={card.name} alignItems="flex-start" pr={0}>
                        <MenuItem {...card} source="import_page" />
                    </Grid>
                ))}
            </Grid>
        </Container>
    )
}
